import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Card } from '@/components/ui/card'
import { formatDistanceToNow, format } from 'date-fns'
import {
	Globe,
	Smartphone,
	Monitor,
	Mail,
	MapPin,
	Clock,
	Link as LinkIcon,
	Languages,
	CheckCircle,
	MessageSquare,
	Star,
	TrendingUp,
	FileText,
	Tablet,
	Laptop,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTranslation } from '@/hooks/useTranslation'

interface UserMetadata {
	email?: string
	name?: string
	browser?: string
	browserVersion?: string
	device?: string
	location?: string
	pageUrl?: string
	language?: string
	referrer?: string
	timezone?: string
	pageTitle?: string
	timestamp?: string
	userAgent?: string
	screenSize?: {
		width: number
		height: number
	}
	screenWidth?: number
	screenHeight?: number
	parsedUA?: {
		browser?: {
			name: string
			version: string
			major: string
		}
		engine?: {
			name: string
			version: string
		}
		os?: {
			name: string
			version: string
		}
		device?: {
			vendor: string
			model: string
		}
		cpu?: any
	}
}

interface Response {
	id: string
	created_at: string
	program_id: string | null
	score: number | null
	response_data: any
	user_metadata: UserMetadata
	external_user_id: string | null
	reporter_name: string | null
	reporter_email: string | null
	user_properties: Record<string, any> | null
	widgets: {
		name: string
		type: string
		question: string
	}
}

interface ResponseDetailViewProps {
	responseId: string
	onClose: () => void
}

const getNPSCategory = (score: number, t: (key: string) => string) => {
	if (score >= 9) return t('responsesTable.promoter')
	if (score >= 7) return t('responsesTable.passive')
	return t('responsesTable.detractor')
}

const getNPSBadgeColor = (score: number) => {
	if (score >= 9)
		return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300 ring-green-500/30'
	if (score >= 7)
		return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300 ring-yellow-500/30'
	return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300 ring-red-500/30'
}

const getCSATLabel = (score: number, t: (key: string) => string) => {
	const labels = [
		t('responsesTable.veryUnsatisfied'),
		t('responsesTable.unsatisfied'),
		t('responsesTable.neutral'),
		t('responsesTable.satisfied'),
		t('responsesTable.verySatisfied'),
	]
	return labels[score - 1] || t('responsesTable.unknown')
}

const getCSATBadgeColor = (score: number) => {
	if (score >= 4)
		return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300 ring-green-500/30'
	if (score === 3)
		return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300 ring-yellow-500/30'
	return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300 ring-red-500/30'
}

const getQuestionIcon = (type: string) => {
	switch (type?.toLowerCase()) {
		case 'text':
		case 'textarea':
			return <MessageSquare className="h-4 w-4 text-blue-500" />
		case 'rating':
			return <Star className="h-4 w-4 text-yellow-500" />
		case 'scale':
		case 'nps':
		case 'csat':
			return <TrendingUp className="h-4 w-4 text-purple-500" />
		default:
			return <MessageSquare className="h-4 w-4 text-gray-500" />
	}
}

const getDeviceTypeInfo = (metadata: UserMetadata) => {
	const parsedUA = (metadata as any).parsedUA
	const screenWidth =
		(metadata as any).screenWidth || metadata.screenSize?.width
	const userAgent = metadata.userAgent || ''

	// Check for mobile indicators in user agent
	const isMobile =
		/Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
			userAgent
		)
	const isTablet =
		/iPad|Tablet|PlayBook|Silk/i.test(userAgent) ||
		(parsedUA?.device?.model && /tablet|ipad/i.test(parsedUA.device.model))

	// Use screen size as additional indicator
	const isSmallScreen = screenWidth && screenWidth < 768

	// Determine device type and icon
	if (isTablet) {
		return {
			type: 'Tablet',
			icon: <Tablet className="h-4 w-4" />,
			details: parsedUA?.device
				? `${parsedUA.device.vendor || ''} ${
						parsedUA.device.model || ''
				  }`.trim()
				: metadata.device,
		}
	} else if (isMobile || isSmallScreen) {
		return {
			type: 'Mobile',
			icon: <Smartphone className="h-4 w-4" />,
			details: parsedUA?.device
				? `${parsedUA.device.vendor || ''} ${
						parsedUA.device.model || ''
				  }`.trim()
				: metadata.device,
		}
	} else {
		return {
			type: 'Desktop',
			icon: <Monitor className="h-4 w-4" />,
			details: parsedUA?.device
				? `${parsedUA.device.vendor || ''} ${
						parsedUA.device.model || ''
				  }`.trim()
				: metadata.device,
		}
	}
}

const Section: React.FC<{
	title: string
	children: React.ReactNode
	className?: string
}> = ({ title, children, className }) => (
	<Card className={cn('p-0 overflow-hidden', className)}>
		<div className="p-4 border-b border-slate-200 dark:border-slate-700">
			<h3 className="font-semibold text-slate-800 dark:text-slate-200">
				{title}
			</h3>
		</div>
		<div className="p-4 space-y-4">{children}</div>
	</Card>
)

const MetadataItem: React.FC<{
	icon: React.ReactNode
	label: string
	value: React.ReactNode | string | null
	isUrl?: boolean
	badge?: boolean
}> = ({ icon, label, value, isUrl, badge }) => {
	if (!value) return null
	return (
		<div className="flex items-start gap-3">
			<div className="mt-1 text-slate-400 dark:text-slate-500">{icon}</div>
			<div className="flex-1">
				<div className="text-xs text-slate-500 dark:text-slate-400">
					{label}
				</div>
				{isUrl && typeof value === 'string' ? (
					<a
						href={value}
						target="_blank"
						rel="noopener noreferrer"
						className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline break-all">
						{value}
					</a>
				) : badge ? (
					<Badge
						variant="secondary"
						className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 text-xs mt-1">
						{value}
					</Badge>
				) : (
					<div className="text-sm text-slate-700 dark:text-slate-300 break-words">
						{value}
					</div>
				)}
			</div>
		</div>
	)
}

const SkeletonLoader: React.FC = () => (
	<div className="p-6 md:p-8 space-y-6 animate-pulse">
		<div className="flex justify-between items-start">
			<div>
				<div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded"></div>
				<div className="h-4 w-64 bg-slate-200 dark:bg-slate-700 rounded mt-2"></div>
			</div>
		</div>
		<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
			<div className="lg:col-span-2 space-y-6">
				<div className="h-40 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
				<div className="h-40 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
			</div>
			<div className="space-y-6">
				<div className="h-32 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
				<div className="h-48 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
			</div>
		</div>
	</div>
)

const ScoreDisplay: React.FC<{
	widget: { type: string; question?: string }
	score: number | null
}> = ({ widget, score }) => {
	const { t } = useTranslation()
	if (score === null) return null

	if (widget.type === 'nps') {
		const category = getNPSCategory(score, t)
		return (
			<div className="space-y-2">
				<p className="text-sm font-medium text-slate-600 dark:text-slate-400">
					{widget.question || t('responseDetailView.howLikelyRecommend')}
				</p>
				<div className="flex items-center gap-3">
					<div
						className={cn(
							'text-3xl font-bold',
							getNPSBadgeColor(score).split(' ')[1]
						)}>
						{score}{' '}
						<span className="text-lg font-normal text-slate-500">/ 10</span>
					</div>
					<Badge
						className={cn(
							'font-medium text-sm',
							getNPSBadgeColor(score),
							'ring-1'
						)}>
						{category}
					</Badge>
				</div>
			</div>
		)
	}

	if (widget.type === 'csat') {
		const label = getCSATLabel(score, t)
		return (
			<div className="space-y-2">
				<p className="text-sm font-medium text-slate-600 dark:text-slate-400">
					{widget.question || t('responseDetailView.howSatisfied')}
				</p>
				<div className="flex items-center gap-3">
					<div
						className={cn(
							'text-3xl font-bold',
							getCSATBadgeColor(score).split(' ')[1]
						)}>
						{score}{' '}
						<span className="text-lg font-normal text-slate-500">/ 5</span>
					</div>
					<Badge
						className={cn(
							'font-medium text-sm',
							getCSATBadgeColor(score),
							'ring-1'
						)}>
						{label}
					</Badge>
				</div>
			</div>
		)
	}

	return null
}

const CustomAnswersDetail = ({ responseData }: { responseData: any }) => {
	const { t } = useTranslation()
	// Check for the NEW structure first
	if (
		responseData?.customAnswers &&
		typeof responseData.customAnswers === 'object'
	) {
		const answers = Object.entries(
			responseData.customAnswers as Record<string, any>
		)
		if (answers.length > 0) {
			return (
				<div className="space-y-4">
					{answers.map(([id, data]) => (
						<div
							key={id}
							className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-200 dark:border-slate-700/50">
							<div className="flex items-start gap-3">
								<div className="mt-1">
									{getQuestionIcon(data.type || 'text')}
								</div>
								<div className="flex-1 space-y-1">
									<p className="text-xs font-medium text-slate-600 dark:text-slate-400">
										{data.questionText}
									</p>
									<p className="text-sm text-slate-800 dark:text-slate-200">
										{data.answer}
									</p>
								</div>
							</div>
						</div>
					))}
				</div>
			)
		}
	}

	// Fallback for OLD structure
	if (
		responseData &&
		typeof responseData === 'object' &&
		!responseData.customAnswers
	) {
		const entries = Object.entries(responseData)
		if (entries.length > 0) {
			return (
				<div className="space-y-4">
					<p className="text-xs text-orange-500 italic">
						{t('responseDetailView.noteDisplayingOlderFormat')}
					</p>
					{entries.map(([key, value]) => (
						<div
							key={key}
							className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-200 dark:border-slate-700/50">
							<p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
								Question ID: <code className="text-xs">{key}</code>
							</p>
							<p className="text-sm text-slate-800 dark:text-slate-200">
								{String(value)}
							</p>
						</div>
					))}
				</div>
			)
		}
	}

	return (
		<p className="text-sm text-slate-500 dark:text-slate-400 italic">
			{t('responseDetailView.noCustomAnswersProvided')}
		</p>
	)
}

const UserMetadataDetail = ({ metadata }: { metadata: UserMetadata }) => {
	if (!metadata) return null

	// Extract detailed info from parsedUA if available
	const parsedUA = (metadata as any).parsedUA
	const browserInfo = parsedUA?.browser
		? `${parsedUA.browser.name} ${parsedUA.browser.version}`
		: metadata.browser
		? `${metadata.browser} ${metadata.browserVersion || ''}`.trim()
		: null
	const osInfo = parsedUA?.os
		? `${parsedUA.os.name} ${parsedUA.os.version || ''}`.trim()
		: null

	// Get device type analysis
	const deviceTypeInfo = getDeviceTypeInfo(metadata)

	// Use screenWidth/screenHeight if available, fallback to screenSize
	const screenInfo =
		(metadata as any).screenWidth && (metadata as any).screenHeight
			? `${(metadata as any).screenWidth}x${(metadata as any).screenHeight}`
			: metadata.screenSize
			? `${metadata.screenSize.width}x${metadata.screenSize.height}`
			: null

	const items = [
		{
			icon: <Mail className="h-4 w-4" />,
			label: 'Email',
			value: metadata.email,
		},
		{
			icon: <Globe className="h-4 w-4" />,
			label: 'Browser',
			value: browserInfo,
		},
		{
			icon: <Laptop className="h-4 w-4" />,
			label: 'Operating System',
			value: osInfo,
		},
		{
			icon: deviceTypeInfo.icon,
			label: 'Device Type',
			value: deviceTypeInfo.type,
		},
		{
			icon: <Smartphone className="h-4 w-4" />,
			label: 'Device Model',
			value: deviceTypeInfo.details,
		},
		{
			icon: <MapPin className="h-4 w-4" />,
			label: 'Location',
			value: metadata.location,
		},
		{
			icon: <Languages className="h-4 w-4" />,
			label: 'Language',
			value: metadata.language,
		},
		{
			icon: <Clock className="h-4 w-4" />,
			label: 'Timezone',
			value: metadata.timezone,
		},
		{
			icon: <LinkIcon className="h-4 w-4" />,
			label: 'Page URL',
			value: metadata.pageUrl,
			isUrl: true,
		},
		{
			icon: <FileText className="h-4 w-4" />,
			label: 'Page Title',
			value: metadata.pageTitle,
		},
		{
			icon: <Monitor className="h-4 w-4" />,
			label: 'Screen Size',
			value: screenInfo,
		},
	]

	return (
		<div className="grid grid-cols-1 gap-y-4">
			{items.map((item, i) => (
				<MetadataItem key={i} {...item} />
			))}
		</div>
	)
}

const UserPropertiesDetail: React.FC<{
	properties: Record<string, any> | null
}> = ({ properties }) => {
	const { t } = useTranslation()
	if (!properties || Object.keys(properties).length === 0) return null
	return (
		<Section title={t('responseDetailView.userProperties')}>
			<div className="space-y-2">
				{Object.entries(properties).map(([key, value]) => (
					<div key={key} className="flex justify-between items-center text-sm">
						<span className="text-slate-500 dark:text-slate-400 capitalize break-words mr-4">
							{key.replace(/([A-Z])/g, ' $1')}
						</span>
						<span className="text-right font-mono text-xs bg-slate-100 dark:bg-slate-700/50 px-2 py-1 rounded break-all">
							{String(value)}
						</span>
					</div>
				))}
			</div>
		</Section>
	)
}

export function ResponseDetailView({
	responseId,
	onClose,
}: ResponseDetailViewProps) {
	const { t } = useTranslation()
	const [isOpen, setIsOpen] = useState(true)

	useEffect(() => {
		return () => {
			const overlay = document.querySelector('[data-radix-portal]')
			if (overlay) {
				overlay.remove()
			}
		}
	}, [])

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open)
		if (!open) {
			onClose()
		}
	}

	const { data: response, isLoading } = useQuery<Response>({
		queryKey: ['response', responseId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from('responses')
				.select(
					`
					*,
					external_user_id,
					reporter_name,
					reporter_email,
					user_properties,
					widgets (
						name,
						type,
						question
					)
				`
				)
				.eq('id', responseId)
				.single()

			if (error) {
				console.error('Error fetching response details:', error)
				throw error
			}

			return data
		},
		enabled: !!responseId,
	})

	if (isLoading || !response) {
		return (
			<Dialog open={isOpen} onOpenChange={handleOpenChange}>
				<DialogContent className="sm:max-w-xl md:max-w-2xl lg:max-w-4xl overflow-y-auto max-h-[90vh]">
					<DialogTitle className="sr-only">{t('responseDetailView.loading')}</DialogTitle>
					<DialogDescription className="sr-only">{t('responseDetailView.loadingDescription')}</DialogDescription>
					<SkeletonLoader />
				</DialogContent>
			</Dialog>
		)
	}

	const displayName =
		response.reporter_name || response.user_metadata?.name || t('responseDetailView.anonymous')
	const displayEmail = response.reporter_email || response.user_metadata?.email

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-xl md:max-w-2xl lg:max-w-4xl overflow-y-auto max-h-[90vh]">
				<DialogTitle className="sr-only">{t('responseDetailView.responseDetails')} #{response.id.slice(0, 8)}</DialogTitle>
				<DialogDescription className="sr-only">
					{t('responseDetailView.detailsForResponse')} #{response.id.slice(0, 8)}
				</DialogDescription>
				<div className="p-1 sm:p-2 md:p-4">
					<div className="flex justify-between items-start mb-6">
						<div>
							<h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">
								{t('responseDetailView.response')}{' '}
								<span className="text-slate-400 dark:text-slate-500">
									#{response.id.slice(0, 8)}
								</span>
							</h2>
							<p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
							
								{format(new Date(response.created_at), 'PP')}
							</p>
						</div>
						<Badge
							variant="secondary"
							className="bg-primary/10 text-primary uppercase font-mono tracking-wider text-xs">
							{response.widgets.type}
						</Badge>
					</div>

					{/* Main Content Grid */}
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
						<div className="lg:col-span-2 space-y-6">
							<Section title={t('responseDetailView.response')}>
								<ScoreDisplay
									widget={response.widgets}
									score={response.score}
								/>
								{response.response_data && (
									<>
										{response.score !== null && (
											<div className="border-t border-slate-200 dark:border-slate-700 my-4"></div>
										)}
										<CustomAnswersDetail
											responseData={response.response_data}
										/>
									</>
								)}
							</Section>
						</div>

						<div className="space-y-6">
							<Section title={t('responseDetailView.respondent')}>
								<div className="flex items-start gap-4">
									<Avatar className="h-12 w-12 flex-shrink-0">
										<AvatarFallback className="text-lg font-semibold">
											{(displayName || 'A').charAt(0).toUpperCase()}
										</AvatarFallback>
									</Avatar>
									<div className="flex-1 space-y-2">
										<div className="flex items-center gap-2">
											<span className="font-semibold text-lg text-slate-800 dark:text-slate-200">
												{displayName}
											</span>
											{response.external_user_id && (
												<Tooltip>
													<TooltipTrigger asChild>
														<Badge 
															variant="secondary" 
															className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 text-xs"
														>
															<CheckCircle className="h-3 w-3 mr-1" />
															{t('responseDetailView.identified')}
														</Badge>
													</TooltipTrigger>
													<TooltipContent>
														<p>{t('responseDetailView.identifiedTooltip')}</p>
													</TooltipContent>
												</Tooltip>
											)}
										</div>
										{displayEmail && (
											<div className="text-sm text-slate-500 dark:text-slate-400">
												{displayEmail}
											</div>
										)}
										{response.external_user_id && (
											<div className="text-xs text-slate-500 dark:text-slate-400">
												ID: <code className="font-mono bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-xs">{response.external_user_id}</code>
											</div>
										)}
									</div>
								</div>
							</Section>

							<Section title={t('responseDetailView.context')}>
								<UserMetadataDetail metadata={response.user_metadata} />
							</Section>

							<UserPropertiesDetail properties={response.user_properties} />
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
